import { TraingeChatbotRuntime } from "./runtime";
import { TraingeChatbotInitOptions } from "./types";

const VISITOR_COOKIE_NAME = "trainge_chatbot_visitor_id";
const VISITOR_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

declare global {
  interface Window {
    TraingeChatbot?: {
      init: (options: TraingeChatbotInitOptions) => Promise<TraingeChatbotRuntime>;
    };
  }
}

async function init(options: TraingeChatbotInitOptions) {
  const runtime = new TraingeChatbotRuntime(normalizeOptions(options));
  await runtime.mount();
  return runtime;
}

function readDatasetOptions(): TraingeChatbotInitOptions | null {
  const script = document.currentScript as HTMLScriptElement | null;
  if (!script?.dataset.botId) return null;

  return {
    botId: script.dataset.botId,
    userId: script.dataset.userId,
    visitorId: script.dataset.visitorId,
    brandId: script.dataset.brandId,
    brandName: script.dataset.brandName,
    brandWelcome: script.dataset.brandWelcome,
    location: script.dataset.location,
    locale: script.dataset.locale,
    apiBase: script.dataset.apiBase,
  };
}

function normalizeOptions(options: TraingeChatbotInitOptions): TraingeChatbotInitOptions {
  const normalizedBrandOptions = {
    brandId: normalizeOptionalDatasetValue(options.brandId),
    brandName: normalizeOptionalDatasetValue(options.brandName),
    brandWelcome: normalizeOptionalDatasetValue(options.brandWelcome),
    location: normalizeOptionalDatasetValue(options.location),
  };
  if (options.userId?.trim()) {
    return {
      ...options,
      ...normalizedBrandOptions,
      userId: options.userId.trim(),
    };
  }

  return {
    ...options,
    ...normalizedBrandOptions,
    userId: undefined,
    visitorId: options.visitorId?.trim() || getOrCreateVisitorId(),
  };
}

function normalizeOptionalDatasetValue(value: string | null | undefined) {
  const normalized = value?.trim();
  return normalized && normalized.toLowerCase() !== "null" ? normalized : undefined;
}

function getOrCreateVisitorId() {
  const existing = readCookie(VISITOR_COOKIE_NAME);
  if (existing) return existing;

  const visitorId =
    typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : fallbackUuid();
  writeCookie(VISITOR_COOKIE_NAME, visitorId);
  return visitorId;
}

function readCookie(name: string) {
  const value = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${encodeURIComponent(name)}=`))
    ?.split("=")[1];

  return value ? decodeURIComponent(value) : "";
}

function writeCookie(name: string, value: string) {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
    value
  )}; Max-Age=${VISITOR_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

function fallbackUuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

window.TraingeChatbot = { init };

const datasetOptions = readDatasetOptions();
if (datasetOptions) {
  void init(datasetOptions);
}
